import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCsrfToken } from '../utils/csrf';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within WishlistProvider');
  }
  return context;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ─── کمک‌کننده‌ها ──────────────────────────────────────────────
  const getUser = () => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  };
  const isLoggedIn = () => !!getUser();

  // ─── بارگذاری علاقه‌مندی‌ها از سرور ─────────────────────────────
  const fetchWishlist = async () => {
    // اگر لاگین نیست، خالی کن
    if (!isLoggedIn()) {
      setWishlistItems([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        const items = Array.isArray(data) ? data : (data.results || []);
        setWishlistItems(items);
        console.log('✅ علاقه‌مندی‌ها از سرور بارگذاری شد:', items.length, 'آیتم');
      } else if (response.status === 401) {
        // توکن منقضی شده - logout کن
        console.warn('⚠️ توکن منقضی شده، لاگ‌اوت می‌کنیم');
        handleLogout();
      } else {
        console.error('❌ خطا در بارگذاری علاقه‌مندی‌ها:', response.status);
        const errorData = await response.json().catch(() => ({}));
        console.error('Error details:', errorData);
      }
    } catch (error) {
      console.error('❌ خطا در اتصال به سرور:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // بارگذاری اولیه
  useEffect(() => {
    fetchWishlist();
  }, []);

  // ─── logout helper ─────────────────────────────────────────────
  const handleLogout = async () => {
    const { apiService } = await import('../services/api');
    await apiService.logout();
    setWishlistItems([]);
  };

  // ─── چک کردن وجود محصول در لیست ─────────────────────────────────
  const isInWishlist = (productId) => {
    return wishlistItems.some(
      item => (item.product?.id ?? item.product_id ?? item.id) === productId
    );
  };

  // ─── افزودن به علاقه‌مندی‌ها ─────────────────────────────────────
  const addToWishlist = async (product) => {
    // 1) شرط لاگین
    if (!isLoggedIn()) {
      return {
        success: false,
        needsLogin: true,
        message: 'برای افزودن به علاقه‌مندی‌ها ابتدا وارد شوید'
      };
    }

    // 2) تکراری نباشد
    if (isInWishlist(product.id)) {
      return {
        success: false,
        message: 'این محصول قبلاً در لیست علاقه‌مندی‌های شما وجود دارد'
      };
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        },
        body: JSON.stringify({ product_id: product.id })
      });

      if (response.ok) {
        const newItem = await response.json();
        setWishlistItems(prev => [...prev, newItem]);
        console.log('✅ محصول به علاقه‌مندی‌ها اضافه شد:', product.name);

        // ✅ اعلان: افزوده به علاقه‌مندی‌ها
        try {
          window.dispatchEvent(new CustomEvent('notify', {
            detail: {
              type: 'promotion',
              title: 'افزوده به علاقه‌مندی‌ها',
              message: `«${product.name || 'محصول'}» به لیست علاقه‌مندی‌های شما اضافه شد`,
            }
          }));
        } catch (_) { /* silent */ }

        return { success: true, message: 'محصول به لیست علاقه‌مندی‌ها اضافه شد' };
      } else if (response.status === 401) {
        // توکن منقضی
        console.warn('⚠️ توکن منقضی شده');
        handleLogout();
        return {
          success: false,
          needsLogin: true,
          message: 'نشست شما منقضی شده، لطفاً دوباره وارد شوید'
        };
      } else {
        const err = await response.json().catch(() => ({}));
        console.error('❌ خطا در افزودن به علاقه‌مندی‌ها:', err);
        return { success: false, message: err.detail || err.message || 'خطا در افزودن محصول' };
      }
    } catch (error) {
      console.error('❌ خطا در اتصال:', error);
      return { success: false, message: 'خطا در اتصال به سرور' };
    } finally {
      setIsLoading(false);
    }
  };

  // ─── حذف از علاقه‌مندی‌ها ────────────────────────────────────────
  const removeFromWishlist = async (productId) => {
    if (!isLoggedIn()) {
      return { success: false, needsLogin: true, message: 'ابتدا وارد شوید' };
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/wishlist/${productId}/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCsrfToken(),
        }
      });

      if (response.ok || response.status === 204) {
        setWishlistItems(prev =>
          prev.filter(item => (item.product?.id ?? item.product_id ?? item.id) !== productId)
        );
        console.log('🗑️ محصول از علاقه‌مندی‌ها حذف شد');
        return { success: true, message: 'محصول از لیست علاقه‌مندی‌ها حذف شد' };
      } else if (response.status === 401) {
        console.warn('⚠️ توکن منقضی شده');
        handleLogout();
        return {
          success: false,
          needsLogin: true,
          message: 'نشست شما منقضی شده، لطفاً دوباره وارد شوید'
        };
      } else {
        const err = await response.json().catch(() => ({}));
        return { success: false, message: err.detail || err.message || 'خطا در حذف محصول' };
      }
    } catch (error) {
      console.error('❌ خطا در اتصال:', error);
      return { success: false, message: 'خطا در اتصال به سرور' };
    } finally {
      setIsLoading(false);
    }
  };

  // ─── toggle ──────────────────────────────────────────────────────
  const toggleWishlist = async (product) => {
    if (!isLoggedIn()) {
      return { success: false, needsLogin: true, message: 'برای این کار ابتدا وارد شوید' };
    }

    if (isInWishlist(product.id)) {
      return await removeFromWishlist(product.id);
    } else {
      return await addToWishlist(product);
    }
  };

  // ─── پاک کردن هنگام logout ───────────────────────────────────────
  const clearWishlist = () => {
    setWishlistItems([]);
  };

  const value = {
    wishlistItems,
    wishlistCount: wishlistItems.length,
    isLoading,
    isLoggedIn,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    clearWishlist,
    fetchWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;


